"use client";

import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import { PortalNav } from "@/components/portal/portal-nav";
import { syncTokenCookie } from "@/lib/auth";
import { loginRequest, NO_IFRAME } from "@/lib/msal-config";
import { portalSession } from "@/lib/portal";

// /login: não é tela de login. O login é o do portal, e quem leva até ele é a
// <sptech-app-nav> (portal sem sessão → login do portal → volta para cá). Aqui só:
//   1. conta MSAL em cache → token → cookie → ?next=;
//   2. senão, espera a sessão do portal e faz o loginRedirect deste app com o e-mail
//      como login_hint: a sessão do Entra ID já existe, e a Microsoft devolve sem
//      tela (portal fora do ar: sem hint, login direto com a Microsoft).
// Se o loginRedirect acabou de ser tentado e não deu conta, mostra um botão em vez
// de repetir (laço).
const ATTEMPT_KEY = "sptech.login.attempt";
const ATTEMPT_WINDOW_MS = 60_000;

const portalNavStyle = { "--portal-nav-height": "48px" } as CSSProperties;

function readNext() {
  const base = "http://app.invalid";
  const raw = new URLSearchParams(window.location.search).get("next") ?? "/";
  const target = raw.startsWith("/") ? new URL(raw, base) : null;
  return target?.origin === base ? `${target.pathname}${target.search}${target.hash}` : "/";
}

function attemptedRecently() {
  try {
    return Date.now() - Number(sessionStorage.getItem(ATTEMPT_KEY) ?? 0) < ATTEMPT_WINDOW_MS;
  } catch {
    return false;
  }
}

function markAttempt(value: number | null) {
  try {
    if (value === null) sessionStorage.removeItem(ATTEMPT_KEY);
    else sessionStorage.setItem(ATTEMPT_KEY, String(value));
  } catch {
    // Sem storage não há como detectar o laço.
  }
}

export function LoginBridge() {
  const { instance, inProgress } = useMsal();
  const started = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (started.current || inProgress !== InteractionStatus.None) return;
    started.current = true;

    void (async () => {
      const account = instance.getAllAccounts()[0];
      if (account) {
        try {
          const result = await instance.acquireTokenSilent({ ...loginRequest, account, ...NO_IFRAME });
          await syncTokenCookie(result.accessToken);
          markAttempt(null);
          window.location.replace(readNext());
          return;
        } catch (error) {
          console.warn("Token silencioso falhou; pedindo um novo login:", error);
        }
      }
      if (attemptedRecently()) {
        setFailed(true);
        return;
      }
      const user = await portalSession();
      markAttempt(Date.now());
      await instance.loginRedirect({ ...loginRequest, loginHint: user?.email });
    })();
  }, [instance, inProgress]);

  const retry = async () => {
    markAttempt(null);
    await instance.loginRedirect({ ...loginRequest, prompt: "select_account" });
  };

  return (
    <div className="flex h-dvh flex-col bg-brand-primary text-brand-neutral" style={portalNavStyle}>
      <PortalNav />
      <main className="grid flex-1 place-items-center px-6 text-center">
        {failed ? (
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm">Não foi possível entrar automaticamente.</p>
            <button
              type="button"
              onClick={retry}
              className="rounded-xl bg-brand-neutral px-4 py-2 text-sm font-medium text-brand-primary cursor-pointer"
            >
              Entrar com Microsoft
            </button>
          </div>
        ) : (
          <p className="text-sm">Entrando…</p>
        )}
      </main>
    </div>
  );
}
